import React from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { Box, Button, DialogContentText, Link, Progress, UnorderedList } from "@carrotfertility/carotene-core"
import { InfoDialog } from "@carrotfertility/carotene-core-x"
import { DbgPaths } from "#/utils/Paths"
import useBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import { reimbursementCategoryNameMessageKeys as Keys } from "#/components/views/reimbursements/reimbursementCategoryNameMessageKeys"
import { getCategorySections } from "#/components/views/reimbursements/digital-benefit-guide/PayingForCareModal"

function coverageLink(href: string) {
  return function CoverageLink(linkContent: React.ReactNode) {
    return (
      <Link target="_blank" href={href}>
        {linkContent}
      </Link>
    )
  }
}

// Other categories use the Your funds journey list.
const SINGLE_JOURNEY_CATEGORIES = new Set<string>([
  Keys.FERTILITY,
  Keys.PRESERVATION,
  Keys.ADOPTION,
  Keys.GC,
  Keys.PREGNANCY,
  Keys.GAC
])

const SingleJourneyDescription = ({ category }: { category: string }): JSX.Element | null => {
  switch (category) {
    case Keys.FERTILITY:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>fertility expenses</link>."
          values={{ link: coverageLink(DbgPaths.FERTILITY_COVERAGE) }}
        />
      )
    case Keys.PRESERVATION:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>fertility preservation expenses</link>."
          values={{ link: coverageLink(DbgPaths.PRESERVATION_COVERAGE) }}
        />
      )
    case Keys.ADOPTION:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>adoption expenses</link>."
          values={{ link: coverageLink(DbgPaths.ADOPTION_COVERAGE) }}
        />
      )
    case Keys.GC:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>gestational surrogacy expenses</link>."
          values={{ link: coverageLink(DbgPaths.GESTATIONAL_SURROGACY_COVERAGE) }}
        />
      )
    case Keys.PREGNANCY:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>pregnancy and postpartum expenses</link>."
          values={{ link: coverageLink(DbgPaths.PREGNANCY_COVERAGE) }}
        />
      )
    case Keys.GAC:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>gender-affirming care expenses</link>."
          values={{ link: coverageLink(DbgPaths.GENDER_AFFIRMING_CARE) }}
        />
      )
    default:
      return null
  }
}

const JourneyListDescription = ({
  category,
  hasQmeCategory
}: {
  category: string
  hasQmeCategory: boolean
}): JSX.Element => {
  const { header, listItems, footer } = getCategorySections(category, hasQmeCategory)
  return (
    <>
      {header}
      {listItems && <UnorderedList>{listItems}</UnorderedList>}
      {footer}
    </>
  )
}

export const CategoryInfoDialog = ({
  category,
  categoryName,
  hasQmeCategory,
  onClose
}: {
  category: string
  categoryName: string
  hasQmeCategory: boolean
  onClose: () => void
}): JSX.Element => {
  const intl = useIntl()
  const { isLoading } = useBenefit()
  return (
    <InfoDialog
      open
      onClose={onClose}
      title={categoryName}
      slotProps={{
        title: { sx: (theme) => ({ marginBlockEnd: theme.spacing(theme.tokens.spacing.sm) }) },
        actions: { sx: (theme) => ({ paddingBlockStart: theme.spacing(theme.tokens.spacing.lg) }) }
      }}
      primaryAction={<Button onClick={onClose}>{intl.formatMessage({ defaultMessage: "Close" })}</Button>}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Progress />
        </Box>
      ) : (
        <DialogContentText component="div">
          {SINGLE_JOURNEY_CATEGORIES.has(category) ? (
            <SingleJourneyDescription category={category} />
          ) : (
            <JourneyListDescription category={category} hasQmeCategory={hasQmeCategory} />
          )}
        </DialogContentText>
      )}
    </InfoDialog>
  )
}
